"use client";

import { useEffect, useRef, useState } from "react";
import type { LiveSnapshot } from "@/lib/lab/data";

/**
 * The live tape that runs above the Lab's console header.
 *
 * Two clocks, on purpose, and this is what makes the tape cheap. The "4s" ages
 * tick every second in the browser by adding elapsed time to the age the server
 * reported, so the rail reads as live continuously without asking the server
 * anything. The snapshot itself is re-fetched rarely, and the numbers snap back
 * to the truth the moment it lands.
 *
 * So the poll is slow and jittered rather than fast and regular:
 *
 *   - 90s base, against a 60s cache entry in lib/lab/live.ts. An earlier
 *     version polled at exactly the cache TTL, which meant a lone reader missed
 *     the cache on every single poll and paid for a full database scan each
 *     time.
 *   - +/- 25% of random jitter, so a hundred tabs opened by a hundred readers do
 *     not line up and hit the server in a burst the moment the entry expires.
 *   - nothing at all while the tab is hidden, which is where an abandoned tab
 *     used to spend its entire life still polling. Coming back into view pulls
 *     immediately if the held snapshot is older than one interval, so the reader
 *     never sees a corrected number arrive late.
 *
 * The displayed clock is the snapshot's own timestamp, never the browser's wall
 * clock: it has to match between server and client render, and "as of" is the
 * honest label for held data anyway.
 */

const POLL_BASE_MS = 90_000;
const POLL_JITTER = 0.25;

/** One interval, randomised, so concurrent readers stay out of phase. */
function nextDelay(): number {
  return Math.round(POLL_BASE_MS * (1 + (Math.random() * 2 - 1) * POLL_JITTER));
}

function ago(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h`;
}

/** "08 Sep 11:32 UTC" from an ISO string, stable across server and client. */
function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][d.getUTCMonth()];
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day} ${month} ${hh}:${mm} UTC`;
}

export default function LiveCrawl({ initial }: { initial: LiveSnapshot }) {
  const [snap, setSnap] = useState(initial);
  const [drift, setDrift] = useState(0);
  const baseRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastPullAt = Date.now();

    /* No `cache: "no-store"`. The route sets its own Cache-Control and the
       whole point is to let the CDN answer a poll the server has already
       answered for someone else. */
    async function pull() {
      try {
        const res = await fetch("/api/lab/live-crawl");
        if (!res.ok) return;
        const body = (await res.json()) as LiveSnapshot;
        if (cancelled) return;
        setSnap(body);
        baseRef.current = Date.now();
        setDrift(0);
      } catch {
        // A dropped poll is not worth surfacing: the ages keep ticking and the
        // next poll corrects them.
      } finally {
        lastPullAt = Date.now();
      }
    }

    function schedule() {
      clearTimeout(timer);
      if (cancelled || document.hidden) return;
      timer = setTimeout(() => {
        void pull().then(schedule);
      }, nextDelay());
    }

    function onVisibility() {
      if (document.hidden) {
        clearTimeout(timer);
        return;
      }
      if (Date.now() - lastPullAt >= POLL_BASE_MS) void pull().then(schedule);
      else schedule();
    }

    document.addEventListener("visibilitychange", onVisibility);
    schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  /* The second-by-second clock. It costs nothing but a re-render, and it is
     what the reader actually perceives as live, so it runs whenever the tape is
     on screen and stops dead when it is not. Nothing is lost by stopping: the
     age is recomputed from baseRef the moment the tab comes back. */
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      setDrift(Math.round((Date.now() - baseRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const bots = snap.bots.slice(0, 6);
  const quiet = bots.length === 0;
  const flag = snap.stale ? "Last known" : quiet ? "Quiet" : "Live";
  const live = !snap.stale && !quiet;

  return (
    <div className="border-b border-white/10 bg-[#08090A]">
      {/* Phone: flag and clock share a row above the rail, because a 390px
          viewport cannot hold all three side by side. */}
      <div className="flex items-center justify-between gap-3 border-b border-white/[.07] px-[18px] py-2 lg:hidden">
        <div className="flex items-center gap-2">
          <span
            className={`block h-1.5 w-1.5 rounded-full ${
              live ? "lab-live-pip bg-[#EEF975]" : "bg-white/25"
            }`}
            aria-hidden="true"
          />
          <span
            className={`font-mono text-[10px] uppercase tracking-[0.14em] ${
              live ? "text-[#EEF975]" : "text-white/40"
            }`}
          >
            {flag}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/40">
          {snap.stale
            ? `As of ${stamp(snap.generatedAt)}`
            : stamp(snap.generatedAt)}
        </span>
      </div>

      <div className="flex items-stretch">
        <div className="hidden shrink-0 items-center gap-2.5 border-r border-white/10 px-[18px] lg:flex">
          <span
            className={`block h-1.5 w-1.5 rounded-full ${
              live ? "lab-live-pip bg-[#EEF975]" : "bg-white/25"
            }`}
            aria-hidden="true"
          />
          <span
            className={`font-mono text-[10.5px] uppercase tracking-[0.14em] ${
              live ? "text-[#EEF975]" : "text-white/40"
            }`}
          >
            {flag}
          </span>
        </div>

        {/* The rail scrolls inside itself, so the page never scrolls sideways. */}
        <div className="min-w-0 flex-1 overflow-x-auto px-[18px] py-[7px] lg:px-5">
          <div className="flex w-max items-center gap-5 lg:gap-[26px]">
            {quiet ? (
              <span className="whitespace-nowrap font-mono text-[11.5px] text-white/45">
                No AI crawler requests in the last {snap.windowMinutes} minutes
              </span>
            ) : (
              bots.map((b) => {
                const secs = Math.max(0, b.secsAgo + drift);
                const fresh = live && secs < 120;
                return (
                  <span
                    key={b.bot}
                    className={`whitespace-nowrap font-mono text-[11.5px] tabular-nums ${
                      fresh ? "text-white/85" : "text-white/40"
                    }`}
                  >
                    {b.bot}
                    <span className="text-white/[.34]"> · {ago(secs)}</span>
                  </span>
                );
              })
            )}
          </div>
        </div>

        <div className="hidden shrink-0 items-center border-l border-white/10 px-5 lg:flex">
          <span className="whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/40">
            {snap.stale
              ? `As of ${stamp(snap.generatedAt)}`
              : stamp(snap.generatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}
